import type { ÉtatMiseÀJour } from '../api.js';

/**
 * Avancement d'une mise à jour, tel qu'on peut honnêtement le montrer.
 *
 * La base ne conserve qu'un état courant : les étapes traversées sont dans le journal, pas dans une
 * colonne. On peut donc situer un run **actif** dans la séquence, mais pas un run terminé en échec
 * — `failed` ne dit pas où il a échoué. Plutôt que de deviner, on ne montre alors aucune barre
 * d'avancement et on renvoie vers le journal, qui porte chaque transition horodatée. Un indicateur
 * qui invente une étape serait pire que pas d'indicateur.
 */

export type AvancementÉtape = 'faite' | 'courante' | 'attente' | 'interrompue';

export interface ÉtapeAffichée {
  clé: ÉtatMiseÀJour;
  libellé: string;
  état: AvancementÉtape;
}

/**
 * Séquence nominale, dans l'ordre où l'orchestrateur la parcourt.
 *
 * `awaiting_restart` n'y figure pas : c'est une **dérivation**, pas une étape de plus. Elle
 * n'existe que sur les hébergements où la relance appartient au fournisseur, et l'inclure
 * partout ajouterait à tous les autres sites une étape qu'ils ne traversent jamais.
 */
export const ÉTAPES: ReadonlyArray<{ clé: ÉtatMiseÀJour; libellé: string }> = [
  { clé: 'prechecks', libellé: 'Vérifications' },
  { clé: 'backing_up', libellé: 'Sauvegarde' },
  { clé: 'updating', libellé: 'Mise à jour' },
  { clé: 'verifying', libellé: 'Contrôle du site' },
];

/** États pour lesquels une barre d'avancement a un sens. */
const SITUABLES: readonly ÉtatMiseÀJour[] = [
  'queued',
  'prechecks',
  'backing_up',
  'updating',
  'awaiting_restart',
  'verifying',
  'success',
  'rolled_back',
];

export function estSituable(status: ÉtatMiseÀJour): boolean {
  return SITUABLES.includes(status);
}

export function avancement(status: ÉtatMiseÀJour): ÉtapeAffichée[] {
  if (!estSituable(status)) return [];

  if (status === 'success') {
    return ÉTAPES.map((étape) => ({ ...étape, état: 'faite' as const }));
  }

  // Retour arrière : tout s'est déroulé jusqu'à la mise à jour, c'est le contrôle du site qui a
  // échoué et déclenché le retour à la version précédente.
  if (status === 'rolled_back') {
    return ÉTAPES.map((étape) => ({
      ...étape,
      état: étape.clé === 'verifying' ? ('interrompue' as const) : ('faite' as const),
    }));
  }

  if (status === 'queued') {
    return ÉTAPES.map((étape) => ({ ...étape, état: 'attente' as const }));
  }

  // En attente d'un redémarrage humain : la mise à jour est faite, le contrôle du site ne peut pas
  // encore avoir lieu. On montre donc exactement cela, sans le faire passer pour un blocage.
  if (status === 'awaiting_restart') {
    return ÉTAPES.map((étape) => ({
      ...étape,
      état: étape.clé === 'verifying' ? ('attente' as const) : ('faite' as const),
    }));
  }

  const position = ÉTAPES.findIndex((étape) => étape.clé === status);
  return ÉTAPES.map((étape, index) => ({
    ...étape,
    état: index < position ? 'faite' : index === position ? 'courante' : 'attente',
  }));
}

/**
 * Un run empêche-t-il le site de recevoir de nouvelles mises à jour ?
 *
 * Reflète `BLOCKING_UPDATE_STATUSES` côté serveur. `interrupted` en fait partie et c'est
 * volontaire : une installation laissée à moitié modifiée ne doit pas en recevoir une seconde
 * par-dessus. Mais ce blocage doit être **visible**, sinon un site cesse de recevoir ses correctifs
 * sans que personne ne le remarque.
 */
const BLOQUANTS: readonly ÉtatMiseÀJour[] = [
  'pending_approval',
  'queued',
  'prechecks',
  'backing_up',
  'updating',
  'awaiting_restart',
  'verifying',
  'interrupted',
];

export function estBloquant(status: ÉtatMiseÀJour): boolean {
  return BLOQUANTS.includes(status);
}

/**
 * Le run attend-il un redémarrage fait à la main ?
 *
 * Ni un succès ni un échec : les fichiers sont installés, le site sert encore l'ancienne version,
 * et rien n'avancera tant que personne n'aura relancé l'application puis validé l'avoir fait.
 */
export function attendRedémarrage(status: ÉtatMiseÀJour): boolean {
  return status === 'awaiting_restart';
}

/** Un run terminé peut-il encore être défait à la main ? */
export function peutRevenirEnArrière(status: ÉtatMiseÀJour, déjàRevenu: boolean): boolean {
  if (déjàRevenu) return false;
  return status === 'failed' || status === 'interrupted';
}
